<template>
  <div v-if="challenge">
    <HeaderTitle single :back-target-func="backToChallenge">
      <template #bottom>{{ challenge.title }}</template>
    </HeaderTitle>
    <div class="md:text-left w-full">
      <GoalElement
        :progress-text="challenge.progressText"
        :target-value="challenge.targetValue"
      />
      <ProgressCircleElement
        v-if="challenge.userStartAt"
        :user-progress-sum-current-interval="
          challenge.userProgressSumCurrentInterval
        "
        :user-progress-sum="challenge.userProgressSum"
        :is-pass-fail-challenge="challenge.isPassFailChallenge"
        :is-team-challenge="false"
        :is-client-challenge="challenge.isTeamChallenge"
        :all-users-progress-sum="challenge.allUsersProgressSum"
        :all-users-progress-sum-current-interval="
          challenge.allUsersProgressSumCurrentInterval
        "
        :placeholder="challenge.placeholder"
        :target-value="challenge.targetValue"
      >
      </ProgressCircleElement>
      <ChallengeDetailTile
        :end-date="challenge.endDate"
        :start-date="challenge.startDate"
        :image="challenge.image"
        :title="challenge.title"
      />
      <ContentSection :content-sections="challenge.contentSections" />
      <FloatingButton
        v-if="
          new Date(challenge.startDate) <= new Date() &&
          challenge.appUserStartDate === null &&
          new Date(challenge.endDate) >= new Date()
        "
        :fixed="true"
        :label="t('challenge.preview.start.cta.label')"
        :icon="ChallengeStartIcon"
        button-type="normal"
        @click="startChallenge()"
      />
      <FloatingButton
        v-else-if="
          challenge.appUserStartDate === null &&
          new Date(challenge.startDate) >= new Date()
        "
        :fixed="true"
        disabled
        button-type="normal"
        :label="
          t('custom.challenge.start.from.button.caption', {
            s: DateTime.fromISO(challenge.startDate, { locale: 'de' }).toFormat(
              'LLLL yyyy'
            ),
          })
        "
        :icon="ChallengeStartIcon"
      />

      <FloatingButtonGroup
        v-if="challenge.appUserStartDate"
        :custom-challenge-id="challenge.id"
        :on-progress-update="updateChallengeProgress"
        :end-date="challenge.endDate"
      />
    </div>
  </div>
  <EditHistoryModal
    v-if="progressModalOpen"
    :id="challenge.id"
    :open="progressModalOpen"
    :is-pass-fail-challenge="challenge.isPassFailChallenge"
    :limit="challenge.limit"
    :hint="challenge.hint"
    :initial-value="challenge.userProgressSumCurrentInterval"
    :date="new Date().toISOString()"
    :placeholder="challenge.placeholder"
    :progress-value-type="progressValueType"
    @close="progressModalOpen = false"
    @update-history="redirectToStatisticsPage"
  />
</template>
<script setup lang="ts">
import HeaderTitle from "../../../components/v2/layout/header/HeaderTitle.vue";

import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { NAVIGATION_CATEGORY_CHALLENGES } from "../../../enum/v2/navigationCategoryType";
import { useNavbarStore } from "../../../store/v2/navbar";
import ChallengeDetailTile from "../../../components/v2/challanges/ChallengeDetailTile.vue";
import { CustomChallengeDetailsDto } from "../../../dto/custom-challenge-details.dto.ts";
import ContentSection from "../../../components/v2/contentSections/ContentSection.vue";
import ChallengeStartIcon from "../../../components/v2/icon/ChallengeStartIcon.vue";
import FloatingButton from "../../../components/v2/layout/button/FloatingButton.vue";
import { startCustomChallenge } from "../../../api/v2/custom-challenge.ts";
import FloatingButtonGroup from "../../../components/v2/layout/button/FloatingButtonGroup.vue";
import GoalElement from "../../../components/v2/challanges/GoalElement.vue";
import ProgressCircleElement from "../../../components/v2/challanges/ProgressCircleElement.vue";
import { fetchCustomChallenge } from "../../../api/v2/custom-challenge.ts";
import { useUserStore } from "../../../store/v2/user";
import { useClientStore } from "../../../store/v2/client";
import EditHistoryModal from "../../../components/v2/challanges/EditHistoryModal.vue";
import { ActivityType } from "../../../enum/v2/activityType.ts";
import { ProgressValueType } from "../../../enum/v2/progress-value-type.ts";
import { DateTime } from "luxon";
import { useNotificationStore } from "../../../store/v2/notification";
import { notifyIfTargetValueJustAchieved } from "../../../plugins/ChallengeNotification";

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const user = useUserStore();
const client = useClientStore();
const navbarStore = useNavbarStore();
const notificationStore = useNotificationStore();

const challenge = ref<CustomChallengeDetailsDto>([]);
const progressModalOpen = ref(false);
const progressValueType = computed<ProgressValueType | undefined>(() => {
  if (
    challenge.value.activityType ===
    ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_STEPS
  ) {
    return ProgressValueType.Steps;
  }

  if (
    challenge.value.activityType ===
    ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_BICYCLE
  ) {
    return ProgressValueType.Kilometers;
  }

  return undefined;
});

onBeforeMount(() => {
  navbarStore.setActiveNavigationCategory(NAVIGATION_CATEGORY_CHALLENGES);
});

onMounted(async () => {
  const challengeId = route.params.challengeId as string;
  const res = await fetchCustomChallenge(
    user.externalUserId,
    client.code,
    challengeId
  );
  const challengeData = res.data;

  if (challengeData) {
    challenge.value = challengeData;
  }
});

watch(
  () => route.params.challengeId,
  async (newId, oldId) => {
    if (newId !== oldId) {
      const res = await fetchCustomChallenge(
        user.externalUserId,
        client.code,
        newId as string
      );
      challenge.value = res.data;
    }
  }
);

async function startChallenge() {
  const res = await startCustomChallenge(
    user.externalUserId,
    route.params.challengeId as string
  );

  if (res) {
    challenge.value = res.data;
  }
}

async function updateChallengeProgress() {
  progressModalOpen.value = true;
}

async function redirectToStatisticsPage(newVal) {
  const previousChallenge = { ...challenge.value };
  const challengeId = route.params.challengeId as string;
  const res = await fetchCustomChallenge(
    user.externalUserId,
    client.code,
    challengeId
  );
  const challengeData = res.data;
  if (challengeData) {
    challenge.value = challengeData;
  }

  notifyIfTargetValueJustAchieved(
    notificationStore,
    t,
    previousChallenge,
    challenge.value
  );

  if (challenge.value.targetValueAchieved) {
    router.push({
      name: "challengeStatistics",
      params: { challengeId: challenge.value.id },
    });
  }
}

function closeModal() {
  router.push({
    name: "challengeStatistics",
    params: { challengeId: challenge.value.id },
  });
}

function backToChallenge() {
  if (
    window.history.state &&
    (window.history.state.fromArticle || window.history.state.fromHistory)
  ) {
    window.history.back();
  } else {
    router.push({
      name: "challenges",
    });
  }
}
</script>
